import React from 'react';
import styled from 'styled-components';

import PrimaryButton from 'crehana-ui/Buttons/PrimaryButton';
import Price from 'crehana-ui/Text/Price';
import { primaryMain } from 'crehana-ui/styles/colors';
import Plus4 from 'crehana-icons/Plus4';
import Pagination from 'crehana-ui/Pagination';

import { useTranslation } from 'Shared/Hooks/useTranslation';
import isTouchDevice from 'Jsx/Utils/isTouchDevice';
import userActivity from 'Utils/userActivity';
import track from 'Utils/crehanaTracking';
import {
  eventSources,
  eventNames,
  setFbEventToLocalStorage,
} from 'Jsx/Utils/facebookTracking/facebookTracking';

import locales from '../../../i18n/localeEs';
import { COURSE_DETAIL_UPSELL_TRACKS } from '../tracks';
import UpsellingCard from './UpsellingCard';

const translations = locales.es;

const CourseCardWrapper = styled.div<{ margin: boolean }>`
  margin-right: ${({ margin }) => (margin ? '16px' : 0)};
  @media (min-width: 768px) {
    margin-right: ${({ margin }) => (margin ? '64px' : 0)};
  }
`;

const IconWrapper = styled.div`
  background: #fff;
  border-radius: 50%;
  height: 34px;
  width: 34px;
  transform: translateX(50%);
  right: 50%;
  top: 34px;
  z-index: 2;
  ${({ theme }) => theme.breakpoints.up('lg')} {
    top: 68px;
  }
`;

const CoursesAndDiscount = styled.div`
  display: flex;
  flex-direction: column;
  ${({ theme }) => theme.breakpoints.up('sm')} {
    box-shadow: -5px 3px 18px rgba(92, 84, 122, 0.18);
    width: 440px;
  }
  ${({ theme }) => theme.breakpoints.up('lg')} {
    width: 100%;
    max-width: 662px;
  }
`;

const TitleWrapper = styled.div`
  ${({ theme }) => theme.breakpoints.up('lg')} {
    width: 100%;
    max-width: 662px;
  }
`;

const UpsellingBody = ({
  prices,
  createCustomBundle,
  sessionHash,
  courses,
  fullRecommendedCoursesArray,
  countryPrefix,
  parentComponent,
  getNextCoursesBundle,
  getPrevCoursesBundle,
  navigationVisibility,
  upsellCoursePosition,
  currentCourse,
  modelNameUsedInRecommendations,
}) => {
  const { t } = useTranslation();

  const totalItems = courses.length;

  React.useEffect(() => {
    window.track(COURSE_DETAIL_UPSELL_TRACKS.RENDER, {
      parentComponent,
      isTouchDevice: isTouchDevice(),
    });
    const recommendations = courses.map(couse => couse.originalId).join(',');
    const customData = {
      item_id: currentCourse.originalId,
      item_type: 'course',
      recommendations,
      origin: 'course_detail',
      model_used: `${modelNameUsedInRecommendations}`,
      total_items: totalItems,
    };

    userActivity.track(userActivity.EVENTS.RECOMMENDATION_VIEW, customData);
  }, []);
  const upsellAction = () => {
    track(track.TYPES.MIXPANEL, COURSE_DETAIL_UPSELL_TRACKS.BUY, {
      price: prices.price,
      currency: prices.currencySymbol,
      upsellCoursePosition,
      page: window.MIXPANEL_PAGE || 'unknown page', //eslint-disable-line
      isTouchDevice: isTouchDevice(),
      parentComponent,
    });

    try {
      if (window.gtag) {
        window.gtag('event', 'upsell', {
          send_to: 'UA-60829200-1',
          event_category: 'PAYG',
          event_label: 'Abre checkout',
        });
      }
    } catch (error) {
      console.log(error);
    }

    createCustomBundle({
      variables: {
        sessionHash,
        courses: courses.map(el => el.originalId),
      },
    }).then(res => {
      if (res.data.customBundleUpsell.success) {
        const { redirectUrl, originalId } = res.data.customBundleUpsell;
        const fbTrackPayload = {
          content_name: 'Arma tu Pack Ideal',
          content_ids: originalId,
          content_type: 'custom_bundle',
          value: prices.price,
          currency: prices.currencySymbol,
        };

        setFbEventToLocalStorage({
          eventName: eventNames.GO_TO_CHECKOUT,
          sourcePage: eventSources.COURSE_DETAIL,
          fbTrackPayload,
        });

        userActivity.track(
          userActivity.EVENTS.RECOMMENDATION_POSITION_CHECKOUT,
          {
            item_id: currentCourse.originalId,
            item_type: 'course',
            recommendations: fullRecommendedCoursesArray
              .map(el => el.originalId)
              .join(','),
            position_selected: upsellCoursePosition,
            origin: 'Course detail - Frequently Bought Together',
            model_used: modelNameUsedInRecommendations,
          },
          redirectUrl,
        );
      }
    });
  };

  const trackRecommendationPosition = () => {
    userActivity.track(userActivity.EVENTS.RECOMMENDATION_POSITION_CLICK, {
      item_id: currentCourse.originalId,
      item_type: 'course',
      recommendations: fullRecommendedCoursesArray
        .map(el => el.originalId)
        .join(','),
      position_selected: upsellCoursePosition,
      origin: 'Course detail - Frequently Bought Together',
      model_used: modelNameUsedInRecommendations,
    });
  };

  const changePagination = numPage => {
    if (numPage > upsellCoursePosition) {
      getNextCoursesBundle();
      return;
    }
    if (numPage < upsellCoursePosition) {
      getPrevCoursesBundle();
    }
  };

  return (
    <div id="course-detail-upselling-section">
      <TitleWrapper className="block sm:flex sm:items-center md:justify-between mb-16 sm:mb-24">
        <p className="font-h4">
          {t('BUY_FRECUENTLY', {
            defaultValue: translations.BUY_FRECUENTLY,
          })}
        </p>
        {navigationVisibility && (
          <Pagination
            className="hidden lg:flex justify-center"
            currentPage={upsellCoursePosition}
            onPageChange={numPage => changePagination(numPage)}
            totalPages={fullRecommendedCoursesArray.length}
            type="carousel"
          />
        )}
      </TitleWrapper>
      {courses && prices && sessionHash && (
        <CoursesAndDiscount className="flex flex-col bg-white sm:px-24 sm:pt-24 sm:pb-40 lg:p-32 mb-40 sm:mb-48">
          <div className="flex relative items-start justify-between w-full mb-24">
            <IconWrapper className="absolute flex items-center justify-center">
              <Plus4 className="sm:hidden" color={primaryMain} size={30} />
              <Plus4
                className="hidden sm:block"
                color={primaryMain}
                size={38}
              />
            </IconWrapper>
            {courses.map((c, i) => (
              <CourseCardWrapper
                className="w-1/2"
                margin={i === 0}
                key={c.originalId}
              >
                <UpsellingCard
                  countryPrefix={countryPrefix}
                  trackRecommendationPosition={
                    currentCourse.originalId !== c.originalId
                      ? trackRecommendationPosition
                      : undefined
                  }
                  {...c}
                />
              </CourseCardWrapper>
            ))}
          </div>
          <div className="w-full lg:flex lg:items-center">
            {/* Prices */}
            <div className="flex items-center flex-shrink-0 md:mb-8 lg:w-1/2">
              <Price
                currency={prices.currencySymbol}
                discount={`${prices.discount}% dto.`}
                price={prices.price}
                realPrice={prices.realPrice}
                realPriceColor="gray-dark"
              />
            </div>
            {/* Buy button */}
            <PrimaryButton
              className="mt-12 lg:mt-0 lg:ml-auto w-full"
              size="l"
              full
              label={t('BUY_NOW', {
                defaultValue: translations.BUY_NOW,
              })}
              onClick={upsellAction}
            />
          </div>
          {navigationVisibility && (
            <Pagination
              className="mt-16 lg:hidden justify-center"
              currentPage={upsellCoursePosition}
              onPageChange={numPage => changePagination(numPage)}
              totalPages={fullRecommendedCoursesArray.length}
              type="carousel"
            />
          )}
        </CoursesAndDiscount>
      )}
    </div>
  );
};

export default UpsellingBody;
